import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { shortDate } from "@/lib/format";
import { toast } from "sonner";
import { MusicNotes, FilmSlate, YoutubeLogo, UploadSimple, Trash, PlayCircle, Broadcast } from "@phosphor-icons/react";

const ytId = (url) => {
  const m = url.match(/(?:youtu\.be\/|v=|shorts\/|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
};

function MediaPlayer({ item }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (item.kind === "youtube") return;
    let url;
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/media/${item.id}/file`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        if (alive) setSrc(url);
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [item.id, item.kind]);

  if (item.kind === "youtube") {
    const id = ytId(item.url);
    return id ? (
      <iframe data-testid={`media-yt-${item.id}`} title={item.title} className="aspect-video w-full rounded-lg"
        src={`https://www.youtube.com/embed/${id}`} allowFullScreen />
    ) : <p className="text-xs text-red-500">Invalid link</p>;
  }
  if (!src) return <div className="flex h-16 items-center justify-center text-xs text-muted-foreground">Loading…</div>;
  if (item.kind === "video") return <video data-testid={`media-video-${item.id}`} src={src} controls className="w-full rounded-lg" />;
  return <audio data-testid={`media-audio-${item.id}`} src={src} controls className="w-full" />;
}

export default function MediaStudio() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["media"], queryFn: async () => (await api.get("/media/list")).data });
  const media = data?.media || [];
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [ytUrl, setYtUrl] = useState("");
  const [ytTitle, setYtTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) { toast.error("Choose an MP3 or MP4 file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Media added to your library");
      setFile(null); setTitle("");
      qc.invalidateQueries({ queryKey: ["media"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const addYt = async () => {
    if (!ytUrl.trim()) { toast.error("Paste a YouTube link"); return; }
    setBusy(true);
    try {
      await api.post("/media/youtube", { url: ytUrl.trim(), title: ytTitle.trim() });
      toast.success("YouTube video added");
      setYtUrl(""); setYtTitle("");
      qc.invalidateQueries({ queryKey: ["media"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/media/${id}`);
      qc.invalidateQueries({ queryKey: ["media"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const iconFor = (k) => k === "video" ? FilmSlate : k === "youtube" ? YoutubeLogo : MusicNotes;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Media Studio</h1>
        <p className="mt-2 text-muted-foreground">Upload audio &amp; video or paste a YouTube link, then attach it to a broadcast campaign.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Upload */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><UploadSimple size={18} weight="bold" className="text-primary" /> Upload audio / video</div>
          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-input bg-card px-3 py-2.5 text-sm text-muted-foreground hover:border-primary">
            <UploadSimple size={16} /> {file ? file.name : "Choose MP3, WAV, M4A, MP4, MOV or WEBM…"}
            <input data-testid="media-file" type="file" accept="audio/*,video/*,.mp3,.wav,.m4a,.mp4,.mov,.webm" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <input data-testid="media-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)"
            className="mt-3 w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />
          <button data-testid="media-upload-btn" onClick={upload} disabled={busy}
            className="mt-3 flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            <UploadSimple size={16} weight="bold" /> {busy ? "Uploading…" : "Add to library"}
          </button>
          <p className="mt-2 text-xs text-muted-foreground">Max 60 MB per file.</p>
        </div>

        {/* YouTube */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><YoutubeLogo size={20} weight="fill" className="text-red-500" /> Paste a YouTube link</div>
          <input data-testid="media-yt-url" value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} placeholder="https://youtube.com/watch?v=…"
            className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />
          <input data-testid="media-yt-title" value={ytTitle} onChange={(e) => setYtTitle(e.target.value)} placeholder="Title (optional)"
            className="mt-3 w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />
          <button data-testid="media-yt-btn" onClick={addYt} disabled={busy}
            className="mt-3 flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" style={{ background: "#08111F" }}>
            <YoutubeLogo size={16} weight="fill" /> {busy ? "Adding…" : "Add video"}
          </button>
        </div>
      </div>

      {/* Library */}
      <div>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Your media library</h2>
        {media.length === 0 && <div className="rounded-xl border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">No media yet. Upload a file or add a YouTube link above.</div>}
        <div className="grid gap-4 sm:grid-cols-2">
          {media.map((m) => {
            const Icon = iconFor(m.kind);
            return (
              <div key={m.id} data-testid={`media-${m.id}`} className="rounded-2xl border border-border bg-card p-4">
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent text-primary"><Icon size={18} weight="duotone" /></div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{m.title}</p>
                      <p className="text-xs capitalize text-muted-foreground">{m.kind} · {shortDate(m.created_at)}</p>
                    </div>
                  </div>
                  <button data-testid={`media-delete-${m.id}`} onClick={() => remove(m.id)} className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-destructive"><Trash size={16} /></button>
                </div>
                <MediaPlayer item={m} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
